import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  serverTimestamp,
  increment,
} from "firebase/firestore";

import { Collections } from "./constants";

/**
 * @typedef { import('./db-models').NoSQLTeamInvite } NoSQLTeamInvite
 */

/**
 * @param {import('firebase/firestore').Firestore} db The firestore database
 * @returns {object} The invites collection access functions
 */
export default function invitesCollectionFns(db) {
  /**
   * @param {NoSQLTeamInvite} invite The invite to create (id is the token)
   * @returns {Promise<string>} The invite token
   */
  async function createInvite(invite) {
    const { id: token, ...inviteData } = invite;

    if (!token) {
      throw new Error("Invite must have an id (token)");
    }

    try {
      const docRef = doc(db, Collections.TEAM_INVITES, token);
      await setDoc(docRef, {
        ...inviteData,
        createdAt: serverTimestamp(),
        deletedAt: null,
      });
      return token;
    } catch (error) {
      console.error("Failed to create invite:", error);
      throw error;
    }
  }

  /**
   * @param {string} token The invite token (document ID)
   * @returns {Promise<NoSQLTeamInvite|null>} The invite or null
   */
  async function getInvite(token) {
    try {
      const docRef = doc(db, Collections.TEAM_INVITES, token);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists() || docSnap.data().deletedAt) {
        return null;
      }

      return { id: docSnap.id, ...docSnap.data() };
    } catch (error) {
      console.error("Failed to get invite:", error);
      throw error;
    }
  }

  /**
   * @param {string} token The invite token
   * @returns {Promise<void>}
   */
  async function incrementUseCount(token) {
    try {
      const docRef = doc(db, Collections.TEAM_INVITES, token);
      await updateDoc(docRef, {
        useCount: increment(1),
      });
    } catch (error) {
      console.error("Failed to increment invite use count:", error);
      throw error;
    }
  }

  /**
   * @param {string} token The invite token
   * @param {Partial<NoSQLTeamInvite>} updates The fields to update
   * @returns {Promise<void>}
   */
  async function updateInvite(token, updates) {
    try {
      const docRef = doc(db, Collections.TEAM_INVITES, token);
      await updateDoc(docRef, updates);
    } catch (error) {
      console.error("Failed to update invite:", error);
      throw error;
    }
  }

  /**
   * @param {string} token The invite token
   * @param {string} deletedBy The user who deleted
   * @returns {Promise<void>}
   */
  async function deleteInvite(token, deletedBy) {
    try {
      const docRef = doc(db, Collections.TEAM_INVITES, token);
      await updateDoc(docRef, {
        deletedAt: serverTimestamp(),
        deletedBy,
        isActive: false,
      });
    } catch (error) {
      console.error("Failed to delete invite:", error);
      throw error;
    }
  }

  /**
   * @param {string} teamId The team ID
   * @returns {Promise<NoSQLTeamInvite[]>} List of active invites for the team
   */
  async function listTeamInvites(teamId) {
    try {
      const q = query(
        collection(db, Collections.TEAM_INVITES),
        where("teamId", "==", teamId),
        where("isActive", "==", true),
        where("deletedAt", "==", null),
      );

      const snapshot = await getDocs(q);
      const invites = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

      invites.sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() || 0;
        const bTime = b.createdAt?.toMillis?.() || 0;
        return bTime - aTime;
      });

      return invites;
    } catch (error) {
      console.error("Failed to list team invites:", error);
      throw error;
    }
  }

  return {
    createInvite,
    getInvite,
    incrementUseCount,
    updateInvite,
    deleteInvite,
    listTeamInvites,
  };
}
